import { ENV } from "gatsby-env-variables";

export type DevReportEnvironment = "dev" | "staging";

/**
 * The API refuses reports outside dev and staging, so the button follows the
 * same rule rather than offering an action that always fails.
 */
export const resolveDevReportEnvironment = (
  env: unknown
): DevReportEnvironment | null =>
  env === "dev" || env === "staging" ? env : null;

export const devReportEnvironment = (): DevReportEnvironment | null =>
  resolveDevReportEnvironment(ENV);

const BROWSERS = [
  { name: "Edge", pattern: /Edg(?:e|A|iOS)?\/(\d+)/ },
  { name: "Opera", pattern: /OPR\/(\d+)/ },
  { name: "Samsung Internet", pattern: /SamsungBrowser\/(\d+)/ },
  { name: "Firefox", pattern: /(?:Firefox|FxiOS)\/(\d+)/ },
  { name: "Chrome", pattern: /(?:Chrome|CriOS)\/(\d+)/ },
  { name: "Safari", pattern: /Version\/(\d+)[\d.]*.*Safari/ },
];

const OPERATING_SYSTEMS = [
  { name: "iOS", pattern: /(?:iPhone|iPad|iPod).*?OS (\d[\d_]*)/ },
  { name: "Android", pattern: /Android (\d[\d.]*)/ },
  { name: "macOS", pattern: /Mac OS X (\d[\d_]*)/ },
  // Windows 11 also reports NT 10.0, so the NT version is left as it comes
  // rather than translated into a marketing version it cannot distinguish.
  { name: "Windows NT", pattern: /Windows NT ([\d.]+)/ },
  { name: "Linux", pattern: /(?:X11|Linux)()/ },
];

const DEVICE_MODEL_MAX_LENGTH = 100;
const OS_VERSION_MAX_LENGTH = 50;
const ROUTE_NAME_MAX_LENGTH = 255;

const firstMatch = (
  userAgent: string,
  candidates: { name: string; pattern: RegExp }[]
): { name: string; version: string } | null => {
  for (const candidate of candidates) {
    const match = candidate.pattern.exec(userAgent);
    if (match) {
      return { name: candidate.name, version: (match[1] ?? "").replace(/_/g, ".") };
    }
  }

  return null;
};

interface DescribeBrowserArgs {
  userAgent: string;
  viewport?: { width: number; height: number };
}

interface BrowserDescription {
  deviceModel: string;
  osVersion: string;
}

/**
 * Mobile sends a real device model; the closest web equivalent a reviewer can
 * act on is the browser plus the viewport the bug was seen at.
 */
export const describeBrowser = ({
  userAgent,
  viewport,
}: DescribeBrowserArgs): BrowserDescription => {
  const browser = firstMatch(userAgent, BROWSERS);
  const os = firstMatch(userAgent, OPERATING_SYSTEMS);

  const browserLabel = browser
    ? [browser.name, browser.version].filter(Boolean).join(" ")
    : "Unknown browser";
  const size = viewport ? `${viewport.width}×${viewport.height}` : "";

  return {
    deviceModel: [browserLabel, size]
      .filter(Boolean)
      .join(" · ")
      .slice(0, DEVICE_MODEL_MAX_LENGTH),
    osVersion: (os
      ? [os.name, os.version].filter(Boolean).join(" ")
      : "Unknown OS"
    ).slice(0, OS_VERSION_MAX_LENGTH),
  };
};

/** The page the report was filed from, standing in for mobile's screen name. */
export const describeRoute = (location: {
  pathname: string;
  search: string;
}): string =>
  `${location.pathname}${location.search}`.slice(0, ROUTE_NAME_MAX_LENGTH) ||
  "/";

export const resolveAppVersion = (): string =>
  typeof __WEB_APP_VERSION__ === "string" ? __WEB_APP_VERSION__ : "unknown";

/**
 * Gatsby inlines the compilation hash of the build serving the page, which is
 * the only build identifier a deployed web bundle carries.
 */
export const resolveBuildNumber = (
  compilationHash?: string | null
): string => (compilationHash ? compilationHash.slice(0, 8) : "develop");
